import React, { useState, useEffect } from "react";
import { apiPost, apiPatch, apiDelete } from "../api";
import { SvgIcon } from "./SvgIcon";
import { BtnSpinner } from "./BtnSpinner";

export interface LabelData {
  id?: number;
  imapFolder: string;
  color: string;
  icon: string;
}

const ICON_OPTIONS = [
  "tag", "star", "person", "send", "bell", "gear", "credit-card", "search",
  "envelope", "clock-history", "pin-angle", "edit", "security", "external-client", "window-stack"
];

const COLOR_OPTIONS = [
  "#6366f1", "#10b981", "#f59e0b", "#ef4444",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6"
];

export function LabelModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (label: LabelData) => void;
  onDelete?: () => void;
  existingLabel?: LabelData;
}) {
  const isEdit = !!props.existingLabel;
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("tag");
  const [color, setColor] = useState("#6366f1");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    if (props.isOpen) {
      setName(props.existingLabel?.imapFolder ?? "");
      setIcon(props.existingLabel?.icon ?? "tag");
      setColor(props.existingLabel?.color ?? "#6366f1");
      setSubmitting(false);
      setError(null);
      setDeleteConfirm(false);
    }
  }, [props.isOpen, props.existingLabel]);

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please enter a label name.");
      return;
    }

    setSubmitting(true);
    setError(null);

    if (isEdit && props.existingLabel?.id) {
      const res = await apiPatch<{ ok: boolean }>(`/v1/labels/${props.existingLabel.id}`, {
        name: trimmed,
        color,
        icon
      });
      setSubmitting(false);
      if (!res.ok) {
        setError(res.error || "Failed to update label.");
        return;
      }
      props.onSave({ ...props.existingLabel, imapFolder: trimmed, color, icon });
    } else {
      const res = await apiPost<LabelData>("/v1/labels", { name: trimmed, color, icon });
      setSubmitting(false);
      if (!res.ok) {
        setError(res.error || "Failed to create label.");
        return;
      }
      props.onSave(res.data);
    }

    props.onClose();
  }

  async function handleDelete() {
    if (!props.existingLabel?.id) return;
    setSubmitting(true);
    setError(null);
    const res = await apiDelete<{ ok: boolean }>(`/v1/labels/${props.existingLabel.id}`);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Failed to delete label.");
      return;
    }
    props.onDelete?.();
    props.onClose();
  }

  if (!props.isOpen) return null;

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard" style={{ width: "min(420px, 92vw)" }}>
        <div className="modalHeader">
          <div className="modalTitle">{isEdit ? "Edit label" : "Add label"}</div>
          <button className="iconBtn" onClick={props.onClose} title="Close" disabled={submitting}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        <div className="modalBody" style={{ padding: "16px 20px" }}>
          {/* Preview */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", padding: "12px", background: "var(--hover-bg)", borderRadius: "8px" }}>
            <div style={{
              width: 34, height: 34, borderRadius: "999px",
              border: "1px solid var(--line2)", background: "var(--hover-bg)",
              display: "grid", placeItems: "center", color, fontSize: 0
            }}>
              <SvgIcon name={icon} title="" style={{ width: 18, height: 18, display: "block" }} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--text)" }}>
              {name.trim() || "Label name"}
            </span>
          </div>

          {/* Name input */}
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
            Name
          </label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            placeholder="e.g. Projects, Receipts, Travel"
            disabled={submitting}
            autoFocus
            maxLength={60}
            style={{ width: "100%", boxSizing: "border-box", marginBottom: "16px" }}
          />

          {/* Icon picker */}
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
            Icon
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "16px" }}>
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                type="button"
                onClick={() => setIcon(ic)}
                disabled={submitting}
                style={{
                  width: 36, height: 36, borderRadius: "6px",
                  border: ic === icon ? "2px solid var(--accent)" : "1px solid var(--control-border)",
                  background: ic === icon ? "var(--surface2)" : "transparent",
                  display: "grid", placeItems: "center",
                  cursor: "pointer", color: ic === icon ? color : "var(--muted)",
                  fontSize: 0
                }}
                title={ic}
              >
                <SvgIcon name={ic} title="" style={{ width: 16, height: 16, display: "block" }} />
              </button>
            ))}
          </div>

          {/* Color picker */}
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
            Color
          </label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                disabled={submitting}
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: c, border: c === color ? "3px solid var(--text)" : "2px solid transparent",
                  cursor: "pointer", outline: c === color ? "2px solid var(--accent)" : "none",
                  outlineOffset: "2px"
                }}
                title={c}
              />
            ))}
          </div>

          {error && (
            <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "10px" }}>
              {error}
            </div>
          )}
        </div>

        <div className="modalFooter" style={{ padding: "12px 20px" }}>
          {isEdit && !deleteConfirm && (
            <button
              className="btn"
              style={{ background: "#ef4444", color: "white" }}
              onClick={() => setDeleteConfirm(true)}
              disabled={submitting}
            >
              Delete
            </button>
          )}
          {deleteConfirm && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--muted)" }}>
              <span>Are you sure? This will delete the folder and all emails in it.</span>
              <button
                className="btn"
                style={{ background: "#ef4444", color: "white" }}
                onClick={handleDelete}
                disabled={submitting}
              >
                <BtnSpinner loading={submitting}>Yes, delete</BtnSpinner>
              </button>
              <button
                className="btn"
                onClick={() => setDeleteConfirm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          )}
          <div style={{ flex: 1 }} />
          {!deleteConfirm && (
            <>
              <button className="btn" onClick={props.onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={handleSave}
                disabled={submitting || !name.trim()}
              >
                <BtnSpinner loading={submitting}>{isEdit ? "Save" : "Create"}</BtnSpinner>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
